import { useEffect } from 'react';
import type { MatchResult, PlayerScore } from '@minigame-race/contracts';
import { GhostButton, PrimaryButton, Screen } from '../components/Chrome.js';
import { MINIGAME_META } from '../race/registry.js';
import { color } from '../theme.js';
import { play as playSound } from '../lib/sound.js';

/**
 * Win / loss / tie / voided. The mockups only cover win and loss, so tie and
 * voided reuse the same layout with a neutral treatment rather than inventing
 * a new one.
 */
export function ResultScreen({
  result,
  youId,
  rematch,
  onRematch,
  onNewMatch,
  onLobby,
}: {
  result: MatchResult;
  youId: string;
  /** Live rematch state from the server, or null if none is possible. */
  rematch: { youOffered: boolean; opponentOffered: boolean; opponentAvailable: boolean } | null;
  /** Offer (or withdraw) a rematch vs the SAME opponent. */
  onRematch: () => void;
  /** Fall back to matchmaking for a new opponent. */
  onNewMatch: () => void;
  onLobby: () => void;
}) {
  const [pa, pb] = result.players;
  const you = pa.userId === youId ? pa : pb;
  const rival = pa.userId === youId ? pb : pa;

  const outcome: 'win' | 'loss' | 'tie' | 'voided' = result.voided
    ? 'voided'
    : result.winnerId === null
      ? 'tie'
      : result.winnerId === youId
        ? 'win'
        : 'loss';

  const eloChange = result.elo.find((e) => e.userId === youId);
  const forfeited = result.forfeitedBy !== undefined;

  useEffect(() => {
    if (outcome === 'win') playSound('win');
    else if (outcome === 'loss') playSound('lose');
  }, [outcome]);

  const headline = {
    win: { text: 'VICTORY', tint: color.lime, sub: 'RACE COMPLETE', glow: 'rsGlowW 2s ease-in-out infinite' },
    loss: { text: 'DEFEAT', tint: color.redBright, sub: 'RACE COMPLETE', glow: undefined },
    tie: { text: 'DEAD HEAT', tint: color.amberBright, sub: 'NO RATING CHANGE', glow: undefined },
    voided: { text: 'VOIDED', tint: 'rgba(234,255,234,.6)', sub: 'BOTH PLAYERS LEFT', glow: undefined },
  }[outcome];

  return (
    <Screen brackets="diagonal">
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'clamp(18px,3.4cqmin,40px)',
          overflowY: 'auto',
        }}
      >
        <div style={{ width: 'min(94cqw,620px)', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* headline */}
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, letterSpacing: '.4em', fontWeight: 600, color: `${headline.tint}b0` }}>{headline.sub}</span>
            <span
              style={{
                fontWeight: 700,
                fontSize: 'clamp(38px,9cqmin,72px)',
                lineHeight: 0.95,
                letterSpacing: '.02em',
                color: headline.tint,
                animation: headline.glow,
                textShadow: outcome === 'loss' ? '0 0 24px rgba(255,46,77,.4)' : undefined,
              }}
            >
              {headline.text}
            </span>
            {forfeited && (
              <span style={{ fontSize: 12, letterSpacing: '.1em', color: color.amberBright, fontWeight: 600, marginTop: 4 }}>
                {result.forfeitedBy === youId ? 'YOU FORFEITED' : 'OPPONENT FORFEITED'}
              </span>
            )}
            {!result.ranked && !result.voided && (
              <span style={{ fontSize: 11, letterSpacing: '.16em', color: 'rgba(234,255,234,.4)', fontWeight: 600, marginTop: 4 }}>
                FRIEND MATCH · UNRANKED
              </span>
            )}
          </div>

          {/* totals */}
          {!result.voided && (
            <div style={{ display: 'flex', alignItems: 'stretch', gap: 12 }}>
              <TotalCard score={you} label="YOU" winner={result.winnerId === you.userId} side="you" />
              <TotalCard score={rival} label={rival.displayName} winner={result.winnerId === rival.userId} side="opponent" />
            </div>
          )}

          {/* per-segment breakdown */}
          {!result.voided && you.segments.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 1fr', gap: 10, padding: '0 12px' }}>
                <span style={{ fontSize: 10, letterSpacing: '.18em', fontWeight: 600, color: 'rgba(234,255,234,.4)' }}>MINIGAME</span>
                <span style={{ fontSize: 10, letterSpacing: '.18em', fontWeight: 600, color: 'rgba(166,255,46,.7)', textAlign: 'right' }}>YOU</span>
                <span style={{ fontSize: 10, letterSpacing: '.18em', fontWeight: 600, color: 'rgba(34,224,255,.7)', textAlign: 'right' }}>RIVAL</span>
              </div>

              {you.segments.map((seg, i) => {
                const theirs = rival.segments[i];
                const youBetter = theirs !== undefined && seg.points > theirs.points;
                const theyBetter = theirs !== undefined && theirs.points > seg.points;
                return (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.6fr 1fr 1fr',
                      gap: 10,
                      alignItems: 'center',
                      padding: '11px 12px',
                      border: '1px solid rgba(234,255,234,.08)',
                      borderRadius: 10,
                      background: 'rgba(255,255,255,.02)',
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                      <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'rgba(234,255,234,.4)', fontSize: '.8em' }}>
                        {String(seg.segmentIndex + 1).padStart(2, '0')}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {MINIGAME_META[seg.minigameId].title}
                      </span>
                      {seg.penaltyMs > 0 && (
                        <span style={{ fontSize: '.7em', color: color.amber, whiteSpace: 'nowrap' }}>
                          +{Math.round(seg.penaltyMs / 1000)} pen
                        </span>
                      )}
                    </span>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 15, textAlign: 'right', color: youBetter ? color.lime : 'rgba(234,255,234,.55)' }}>
                      {seg.points}
                    </span>
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 15, textAlign: 'right', color: theyBetter ? color.cyan : 'rgba(234,255,234,.55)' }}>
                      {theirs ? theirs.points : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* elo + actions */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
            {eloChange && eloChange.delta !== 0 ? (
              <div
                style={{
                  flex: 1,
                  minWidth: 190,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 15,
                  border: `1px solid ${eloChange.delta > 0 ? 'rgba(166,255,46,.25)' : 'rgba(255,46,77,.22)'}`,
                  borderRadius: 12,
                  background: eloChange.delta > 0 ? 'rgba(166,255,46,.05)' : 'rgba(255,46,77,.04)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'rgba(234,255,234,.45)', fontWeight: 600 }}>ELO</span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, fontSize: 20, color: 'rgba(234,255,234,.6)' }}>{eloChange.before}</span>
                </div>
                <span style={{ fontSize: 22, color: eloChange.delta > 0 ? color.lime : color.redBright }}>›</span>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 10, letterSpacing: '.16em', color: eloChange.delta > 0 ? color.lime : color.redBright, fontWeight: 700 }}>
                    {eloChange.delta > 0 ? '+' : '−'}
                    {Math.abs(eloChange.delta)}
                  </span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 26, color: eloChange.delta > 0 ? color.lime : color.redBright }}>
                    {eloChange.after}
                  </span>
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, minWidth: 190, padding: 15, border: '1px solid rgba(234,255,234,.1)', borderRadius: 12, background: 'rgba(255,255,255,.02)', textAlign: 'center' }}>
                <span style={{ fontSize: 12, color: 'rgba(234,255,234,.5)', fontWeight: 600, letterSpacing: '.08em' }}>NO RATING CHANGE</span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1.4, minWidth: 220 }}>
              {/* Rematch vs the same opponent, unless they've left. */}
              {rematch === null || rematch.opponentAvailable ? (
                <>
                  <PrimaryButton onClick={onRematch} style={{ padding: 17 }}>
                    {rematch?.youOffered
                      ? rematch.opponentOffered
                        ? 'STARTING…'
                        : 'WAITING FOR RIVAL…'
                      : rematch?.opponentOffered
                        ? 'RIVAL WANTS A REMATCH — ACCEPT'
                        : 'REMATCH'}
                  </PrimaryButton>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <GhostButton onClick={onNewMatch} style={{ flex: 1, padding: 15 }}>
                      NEW MATCH
                    </GhostButton>
                    <GhostButton onClick={onLobby} style={{ flex: 1, padding: 15 }}>
                      LOBBY
                    </GhostButton>
                  </div>
                </>
              ) : (
                // Opponent has left — no rematch possible, offer matchmaking.
                <>
                  <span style={{ textAlign: 'center', fontSize: 12, color: 'rgba(234,255,234,.45)', marginBottom: 2 }}>
                    Your rival left.
                  </span>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <PrimaryButton onClick={onNewMatch} style={{ flex: 1, padding: 15 }}>
                      NEW MATCH
                    </PrimaryButton>
                    <GhostButton onClick={onLobby} style={{ flex: 1, padding: 15 }}>
                      LOBBY
                    </GhostButton>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Screen>
  );
}

function TotalCard({ score, label, winner, side }: { score: PlayerScore; label: string; winner: boolean; side: 'you' | 'opponent' }) {
  const tint = side === 'you' ? color.lime : color.cyan;
  const accent = side === 'you' ? '166,255,46' : '34,224,255';
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: 18,
        border: winner ? `1.5px solid rgba(${accent},.55)` : '1px solid rgba(234,255,234,.14)',
        borderRadius: 12,
        background: winner ? `rgba(${accent},.08)` : 'rgba(255,255,255,.02)',
        boxShadow: winner ? `0 0 24px rgba(${accent},.15)` : undefined,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: '.05em', color: winner && side === 'opponent' ? color.cyanBright : 'rgba(234,255,234,.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label.toUpperCase()}
        </span>
        {winner && (
          <span style={{ fontSize: 10, letterSpacing: '.14em', fontWeight: 700, color: side === 'you' ? color.limeInk : color.cyanInk, background: tint, padding: '2px 6px', borderRadius: 4, flex: 'none' }}>
            WINNER
          </span>
        )}
      </div>
      <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 800, fontSize: 'clamp(26px,6cqmin,46px)', lineHeight: 1, color: winner ? tint : 'rgba(234,255,234,.6)' }}>
        {score.totalPoints}
        <span style={{ fontSize: '.4em', letterSpacing: '.1em', color: winner ? `${tint}99` : 'rgba(234,255,234,.35)' }}> PTS</span>
      </span>
    </div>
  );
}
